// taken from @sprint/sprint-react-components/profile_lists/SavedProfilesGrid.tsx
import ListAddEditModal, { ViewAddEditModalType } from '@client/CommonComponents/src/profile_lists/ViewAddEditModal';
import { UserPermissionsContext } from '@client/Context/UserPermissions';
import {
    AppEvent,
    BasicFilter,
    DataGrid,
    DataGridActionBar,
    EventBusInstance,
    ExtendedColumn,
    FilterBar,
    NonSelectableRow,
    OptionTypeBase,
    PeopleTableType,
    SavedProfile,
    SavedView,
    SearchQuery,
    SearchResult,
    SearchResultCounts,
    User,
    UserState,
    isProfileOwnedByMe,
} from '@sprint/sprint-react-components';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import './SavedViewsGrid.scss';

interface Props {
    onFilterChange: (filters: Map<string, OptionTypeBase>) => void;
    filters: BasicFilter[];
    defaultSortColumn?: string;
    reload?: boolean;
    loading?: boolean;
    onUpdateProfile: (profile: SavedProfile) => Promise<boolean>;
    peopleTableType: PeopleTableType | undefined;
    user: User | UserState;
    onDeleteSelectedRows: ((ids: number[]) => Promise<boolean>) | undefined;
    onGetRows: (query: SearchQuery) => Promise<SearchResult<any, SearchResultCounts> | null>;
    columns: ExtendedColumn[];
    favouriteText?: string | undefined;
    addEditModalType?: ViewAddEditModalType;
    checkProfileNameIsUnique?: (profileName: string, profileId?: number) => Promise<boolean>;
    getAvailableFolders?: (filter: string, loadedOptions: any, page: number) => Promise<any>;
    entityTypeSingular: string;
    entityTypePlural: string;
}

const SavedViewsGrid: FunctionComponent<Props> = (props: Props) => {
    const [filter, setFilter] = useState('');
    const [editModalShown, setEditModalShown] = useState(false);
    const [editModalProfile, setEditModalProfile] = useState<SavedProfile>();
    const permissions = useContext(UserPermissionsContext);

    useEffect(() => {
        EventBusInstance.subscribe('show-hoverover-component', (event: AppEvent<any>) => {
            if (event.target === 'show-edit-modal') {
                setEditModalProfile(event.message);
                setEditModalShown(true);
            } else if (event.target === 'hide-edit-modal') {
                setEditModalShown(false);
            }
            return;
        });
    }, [editModalShown]);

    const onUpdateRow = async () => {
        throw new Error('Function not implemented.');
    };

    const onCloseAddModal = () => {
        throw new Error('Function not implemented.');
    };

    const onAddButtonClick = () => {
        throw new Error('Function not implemented.');
    };

    // Rows shouldn't be selectable if not 'owned' by current user
    const findNonSelectableRows = (rows: SavedView[]): NonSelectableRow[] | null => {
        return rows
            ?.filter((row: SavedView) => !isProfileOwnedByMe(row, props.user))
            .map((row: SavedView) => ({ id: row.id!, reason: 'Shared ' + props.entityTypeSingular }));
    };

    const modalType = props.addEditModalType ?? ViewAddEditModalType.LIST;

    return (
        <>
            {editModalProfile && (
                <ListAddEditModal
                    modalType={modalType}
                    profile={editModalProfile}
                    modalShown={editModalShown}
                    closeModal={() => setEditModalShown(false)}
                    onSubmit={props.onUpdateProfile}
                    peopleTableType={props.peopleTableType}
                    user={props.user}
                    checkProfileNameIsUnique={props.checkProfileNameIsUnique}
                    favouriteText={props.favouriteText ? props.favouriteText : 'Favourite'}
                    entityTypeSingular={props.entityTypeSingular}
                    getAvailableFolders={props.getAvailableFolders}
                    withSchedule={modalType === ViewAddEditModalType.LIST && permissions.s3DataSync.isEnabled}
                />
            )}

            <DataGridActionBar
                filterPlaceholder={'Search by ' + props.entityTypeSingular.toLowerCase() + ' name'}
                hideAddButton={true}
                counts={<></>}
                onFilterChange={setFilter}
                filter={filter}
                onAddButtonClick={onAddButtonClick}
                alignLeft={true}
            >
                <FilterBar filters={props.filters} onFilterChange={props.onFilterChange} />
            </DataGridActionBar>
            <div style={{ height: '100%', display: 'flex' }}>
                <DataGrid
                    uniqueKey={'savedViewsGrid'}
                    basicFilter={filter}
                    addModal={undefined}
                    showAddModal={false}
                    columns={props.columns}
                    onUpdateRow={onUpdateRow}
                    onCloseAddModal={onCloseAddModal}
                    onGetRows={props.onGetRows}
                    onDeleteSelectedRows={props.onDeleteSelectedRows}
                    entityPlural={props.entityTypePlural}
                    entitySingular={props.entityTypeSingular}
                    nonSelectableRows={findNonSelectableRows}
                    reload={props.reload ?? false}
                    loading={props.loading ?? false}
                    defaultSortColumn={props.defaultSortColumn}
                />
            </div>
        </>
    );
};

export default SavedViewsGrid;
